/**
 * What the free tier includes and what Premium adds — in one place.
 *
 * ## Why this exists
 *
 * There were two different answers to "what do I get for $7.99", and neither matched the code:
 *
 *   - PricingPage listed "Unlimited route simulations" and "Real-time play visualization". Both are
 *     free. It also said "Build custom playbooks", which is not what the feature is called anywhere
 *     in the product (it is the Defense Play Maker, and Save Play).
 *   - AccountPage listed a DIFFERENT four items, including "Priority support", which does not exist.
 *   - Neither page said what the free tier includes, so a visitor could not tell what they would
 *     actually gain.
 *
 * Promising something the product does not do is worse than under-selling: a coach who subscribes for
 * priority support and finds none has been mis-sold. So the lists below describe exactly what the
 * gates in the code enforce, and both pages read from here.
 *
 * ## Keeping it true
 *
 * `PREMIUM_COVERAGES` and `PREMIUM_FORMATIONS` are the ACTUAL gate arrays — CoverageSelector and
 * FormationSelector import them from here rather than declaring their own. So the marketing copy and
 * the enforcement cannot drift: if a coverage is unlocked, it leaves the paywall and the pricing
 * page in the same edit.
 */

import { FormationType } from '../types';
import { getAvailableCoverages, getCoverageDefinition } from '../rules';

/**
 * Coverages behind the paywall. The single source of truth for the coverage gate.
 */
export const PREMIUM_COVERAGES: string[] = ['cover3Match', 'cover4Match', 'cover4Palms', 'cover6'];

/** Formations behind the paywall. */
export const PREMIUM_FORMATIONS: FormationType[] = ['trips'];

/** Display names of the coverages a free account can use, in registry order. */
export function freeCoverageNames(formation: FormationType = 'doubles'): string[] {
  return getAvailableCoverages(formation)
    .filter((coverage) => !PREMIUM_COVERAGES.includes(coverage))
    .map((coverage) => getCoverageDefinition(coverage, formation)?.name ?? coverage);
}

/** Display names of the coverages Premium unlocks, in registry order. */
export function premiumCoverageNames(formation: FormationType = 'doubles'): string[] {
  return getAvailableCoverages(formation)
    .filter((coverage) => PREMIUM_COVERAGES.includes(coverage))
    .map((coverage) => getCoverageDefinition(coverage, formation)?.name ?? coverage);
}

/**
 * What a free account can do. Every line here is something the code genuinely allows.
 */
export function freeFeatures(): string[] {
  return [
    `${freeCoverageNames().join(', ')} coverages`,
    'All 11 receiver routes and 4 back routes',
    'Doubles (2x2) formation',
    'Full-speed and slow-motion playback',
    'Route and coverage play art',
  ];
}

/**
 * What Premium adds. Every line here corresponds to a gate that exists in the code.
 */
export function premiumFeatures(): string[] {
  const names = premiumCoverageNames();
  return [
    `${names.length} match coverages — ${names.join(', ')}`,
    'Trips (3x1) formation',
    'Save and load your plays',
    'Defense Play Maker — build a coverage defender by defender',
  ];
}
